// BeeCell — True honeycomb alcove design
// 7 hexagonal cells: 1 center (bee icon) + 6 surrounding (one per stage)
// Flat-top hexagons, PrimeIcons SVG inside each cell — ZERO emoji

import { useState } from 'react';
import {
  StageRegisteredIcon, StageScoredIcon, StageKnownIcon,
  StageConnectedIcon, StageHiveIcon, StageLovedIcon
} from './icons';

const STAGE_ICONS = [
  StageRegisteredIcon, StageScoredIcon, StageKnownIcon,
  StageConnectedIcon,  StageHiveIcon,   StageLovedIcon
];

// Flat-top hex points — R = circumradius
function hexPoints(cx, cy, R) {
  return Array.from({length:6}, (_,i) => {
    const a = (Math.PI / 180) * (60 * i);
    return `${cx + R * Math.cos(a)},${cy + R * Math.sin(a)}`;
  }).join(' ');
}

export default function BeeCell({ progress = [], size = 240, animated = false }) {
  const [tooltip, setTooltip] = useState(null);
  const W = size, H = size * 0.95;
  const cx = W / 2, cy = H / 2;

  // Flat-top packing
  const R     = W * 0.175;   // outer radius of each hex
  const r     = R * 0.84;    // inner ring
  const gap   = R * 0.04;
  const dist  = (R * 2) * Math.sin(Math.PI / 3) + gap;

  const centers = Array.from({length:6}, (_,i) => {
    const a = (Math.PI / 180) * (30 + 60 * i);
    return { x: cx + dist * Math.cos(a), y: cy + dist * Math.sin(a) };
  });

  // Gold color for completed stages
  const GOLD = '#C9A84C';
  const DIM  = 'rgba(255,255,255,0.06)';
  const DONE_STROKE = GOLD;
  const TODO_STROKE = 'rgba(255,255,255,0.12)';

  return (
    <div style={{ position:'relative', display:'inline-block' }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display:'block', overflow:'visible' }}>
        <defs>
          <filter id="cell-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* ── Ghost hexagons extending beyond frame (comb effect) ── */}
        {Array.from({length:6}, (_,i) => {
          const a = (Math.PI / 180) * (30 + 60 * i);
          const gx = cx + dist * 1.92 * Math.cos(a);
          const gy = cy + dist * 1.92 * Math.sin(a);
          return (
            <polygon key={`g${i}`}
              points={hexPoints(gx, gy, R)}
              fill="none" stroke="rgba(201,168,76,0.05)" strokeWidth="0.8"/>
          );
        })}

        {/* ── Stage cells ── */}
        {centers.map((c, i) => {
          const stage  = progress[i];
          const done   = stage?.completed ?? false;
          const isNext = !done && progress.slice(0,i).every(s=>s?.completed);
          const Icon   = STAGE_ICONS[i];
          const iconS  = R * 0.72;

          return (
            <g key={i}
              style={{ cursor:'pointer' }}
              onMouseEnter={() => setTooltip({ ...stage, idx:i })}
              onMouseLeave={() => setTooltip(null)}
            >
              {/* Outer hex */}
              <polygon
                points={hexPoints(c.x, c.y, R)}
                fill={done ? `${GOLD}14` : DIM}
                stroke={done ? DONE_STROKE : isNext ? 'rgba(201,168,76,0.35)' : TODO_STROKE}
                strokeWidth={done ? '1.2' : '0.8'}
                filter={done ? 'url(#cell-glow)' : undefined}
              />
              {/* Inner hex ring — alcove depth */}
              <polygon
                points={hexPoints(c.x, c.y, r * 0.72)}
                fill="none"
                stroke={done ? `${GOLD}30` : 'rgba(255,255,255,0.04)'}
                strokeWidth="0.6"
              />
              {/* Icon — rendered via foreignObject */}
              <foreignObject
                x={c.x - iconS/2} y={c.y - iconS/2}
                width={iconS} height={iconS}
                style={{ overflow:'visible', pointerEvents:'none' }}
              >
                <div xmlns="http://www.w3.org/1999/xhtml"
                  style={{ width:iconS, height:iconS, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Icon
                    size={Math.round(iconS * 0.62)}
                    color={done ? GOLD : isNext ? 'rgba(201,168,76,0.45)' : 'rgba(255,255,255,0.2)'}
                  />
                </div>
              </foreignObject>
              {/* Checkmark for completed */}
              {done && (
                <g transform={`translate(${c.x + R*0.52},${c.y - R*0.52})`}>
                  <circle r={R*0.22} fill={GOLD} stroke="#0A0A0A" strokeWidth="1"/>
                  <polyline
                    points={`${-R*.1},0 ${-R*.02},${R*.1} ${R*.12},${-R*.1}`}
                    fill="none" stroke="#0A0A0A" strokeWidth="1.2"
                    strokeLinecap="round" strokeLinejoin="round"
                  />
                </g>
              )}
              {/* "Next" ring pulse */}
              {isNext && !done && (
                <polygon
                  points={hexPoints(c.x, c.y, R * 1.06)}
                  fill="none" stroke="rgba(201,168,76,0.18)" strokeWidth="0.7"
                  strokeDasharray="3 3"
                />
              )}
            </g>
          );
        })}

        {/* ── Center cell — Bee icon ── */}
        <polygon
          points={hexPoints(cx, cy, R)}
          fill="rgba(201,168,76,0.1)"
          stroke={GOLD}
          strokeWidth="1.2"
          filter="url(#cell-glow)"
        />
        <polygon
          points={hexPoints(cx, cy, r * 0.72)}
          fill="none"
          stroke={`${GOLD}30`}
          strokeWidth="0.7"
        />
        {/* Bee icon via foreignObject */}
        <foreignObject x={cx - R*0.45} y={cy - R*0.45} width={R*0.9} height={R*0.9} style={{ overflow:'visible' }}>
          <div xmlns="http://www.w3.org/1999/xhtml"
            style={{ width:R*0.9, height:R*0.9, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <BeeIconSVG size={Math.round(R * 0.58)} color={GOLD}/>
          </div>
        </foreignObject>
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position:'absolute', bottom:'calc(100% + 8px)', left:'50%', transform:'translateX(-50%)',
          background:'#1C1C1C', border:'1px solid rgba(201,168,76,0.3)',
          padding:'0.6rem 0.9rem', minWidth:160, zIndex:10, pointerEvents:'none',
        }}>
          <div style={{ fontSize:'0.75rem', fontWeight:600, color:tooltip.completed ? GOLD : '#888', marginBottom:'0.2rem', fontFamily:'Outfit' }}>
            {tooltip.name}
          </div>
          <div style={{ fontSize:'0.62rem', color:'#666', lineHeight:1.5 }}>{tooltip.desc}</div>
          {tooltip.completed && tooltip.reward && (
            <div style={{ fontSize:'0.6rem', color:GOLD, marginTop:'0.25rem', fontStyle:'italic' }}>
              {tooltip.reward}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Bee body icon — custom SVG, no emoji
function BeeIconSVG({ size=24, color='currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"
      style={{ display:'block' }}>
      {/* Body */}
      <ellipse cx="12" cy="13" rx="4.5" ry="6"/>
      {/* Head */}
      <circle cx="12" cy="6.5" r="2"/>
      {/* Wings */}
      <ellipse cx="7" cy="10" rx="3.5" ry="2" transform="rotate(-20 7 10)"/>
      <ellipse cx="17" cy="10" rx="3.5" ry="2" transform="rotate(20 17 10)"/>
      {/* Stripes */}
      <line x1="7.7" y1="11.5" x2="16.3" y2="11.5"/>
      <line x1="7.7" y1="14" x2="16.3" y2="14"/>
      <line x1="8.2" y1="16.5" x2="15.8" y2="16.5"/>
      {/* Antennae */}
      <path d="M10.5 4.5 Q9 2 8 1.5"/>
      <path d="M13.5 4.5 Q15 2 16 1.5"/>
      <circle cx="8" cy="1.5" r="0.6" fill={color} stroke="none"/>
      <circle cx="16" cy="1.5" r="0.6" fill={color} stroke="none"/>
    </svg>
  );
}
